<script lang="ts">
    import vm, { JourneyNode, JourneyTextNode } from '@journeys/Journeys.viewmodel';
    import { computed, ComputedRef, onUnmounted, ref } from 'vue';
    import ScrollableContainer from '@shared/components/ScrollableContainer.vue';
    import TabData from '@cwd/journeys/components/UserInterface/Menues/Tabs/NodeSettingsData.vue';
    import TabConditions from './Tabs/NodeConditions.vue';
    import Tabs from '@shared/components/Tabs.vue';
    import Tab from '@shared/components/Tab.vue';
    import { NodeConditionVariation, NodeType } from '@cwd/Journeys/Enums';
    import { toCamelCase } from '@shared/utils/formatText';
    import NavIcons from '@cwd/Navigation/NavIcons.vue';

    // this is mostly for debugging, otherwise components are Anonymous
    const component = {
        name: 'Menu_NodeSettings',
    };
    export default {
        name: component.name,
    };
</script>

<script lang="ts" setup>
    const { editor } = vm;

    onUnmounted(() => {
        editor.events.removeListener('setselectionkey');
    });

    let selectionIndex = ref(0);
    const selectedItems: ComputedRef<any> = computed(() => {
        let node_list = Object.values<JourneyNode | JourneyTextNode>(editor.tools.sel_nodes),
            point_list = Object.values(editor.tools.sel_points);

        for (let i = 0; i < node_list.length; i++)
            if (node_list[i].id === editor.tools.sel_keyObj) {
                selectionIndex.value = i;
                if (node_list[i].data.type === NodeType.Condition && (node_list[i] as JourneyNode).data.var === NodeConditionVariation.Delay)
                    editor.state.nodeSettingsTab = 'data';
                break;
            }

        return { nodes: node_list, points: point_list };
    });

    editor.events.addListener('setselectionkey', (id: number) => {
        editor.tools.sel_keyObj = id;

        selectedItems.value.nodes.find((n: JourneyNode, idx: number) => {
            if (n.id === id) {
                selectionIndex.value = idx;
                return true;
            }
            return false;
        });
    });

    const hideConditionsForStartNode = computed(() => {
        if (!nodeData.value) return true;
        if (nodeData.value.type !== NodeType.Segment) return false;
        return nodeData.value.delay_d === 0 && nodeData.value.segment.Segments.length === 0;
    });

    const showConditionsTab = computed(() => {
        if (hideConditionsForStartNode.value) return false;
        if (!nodeData.value) return false;

        if (nodeData.value.type === NodeType.Condition && nodeData.value.var === NodeConditionVariation.Delay) return false;
        if (nodeData.value.type === NodeType.Page) return false;
        if (isMailInteractionNode.value) return false;

        return true;
    });

    const isMailInteractionNode = computed(() => {
        if (!nodeData.value) return false;
        if (nodeData.value.type !== NodeType.Condition) return false;
        return (
            nodeData.value.var === NodeConditionVariation.Clicked ||
            nodeData.value.var === NodeConditionVariation.Opened ||
            nodeData.value.var === NodeConditionVariation.Bounced ||
            nodeData.value.var === NodeConditionVariation.Unsubscribed
        );
    });

    const nodeData = computed(() => {
        const node = selectedItems.value.nodes[selectionIndex.value];
        return node?.data;
    });

    const getNodeName = (name: string) => {
        return $Resources['JourneyNode' + toCamelCase(name)];
    };
</script>

<template>
    <div v-if="selectedItems.nodes[selectionIndex]" class="menu-container" :class="editor.state.showNodeMenu && 'show-node-menu'">
        <template v-if="editor.state.nodeSettingsTab === 'data'">
            <ScrollableContainer>
                <div class="d-flex align-items-start gap-sm m-sm-t m-sm-b">
                    <h2 class="h3 m-0-all">
                        <small>
                            {{ getNodeName(nodeData?.name) }}
                        </small>

                        <br />

                        {{ $Resources.JourneyStepLabel + ':' }}
                        {{ nodeData?.title || $Resources.NoNamePlaceholder }}
                    </h2>

                    <button type="button" class="btn btn-subtle m-auto-l" @click="editor.state.showNodeMenu = false">
                        <NavIcons icon="close"></NavIcons>
                        <span class="sr-only">{{ $Resources.CloseButton }}</span>
                    </button>
                </div>

                <template v-if="nodeData?.type === NodeType.Task">
                    <Tabs :key="selectedItems.nodes[selectionIndex]?.id" :query-param="false">
                        <Tab :title="$Resources.JourneyNodeTaskContentTab">
                            <TabData :node="selectedItems.nodes[selectionIndex]" />
                        </Tab>

                        <Tab v-if="showConditionsTab" :title="$Resources.JourneyNodeTaskSegmentTab">
                            <TabConditions :node="selectedItems.nodes[selectionIndex]" />
                        </Tab>
                    </Tabs>
                </template>

                <template v-else>
                    <TabData :node="selectedItems.nodes[selectionIndex]" />

                    <template v-if="showConditionsTab">
                        <TabConditions :node="selectedItems.nodes[selectionIndex]" />
                    </template>
                </template>
            </ScrollableContainer>
        </template>
    </div>
</template>

<style>
    .expand-enter-active,
    .expand-leave-active {
        transition-property: opacity, height;
        transition-duration: 0.3s;
        transition-timing-function: ease;
    }

    .expand-leave-to,
    .expand-enter-from {
        height: 0 !important;
        opacity: 0;
    }

    .menu-container {
        display: flex;
        flex-flow: column;
        position: relative;
        height: 100%;
        max-height: 100vh;
        overflow: hidden;
    }

    .static-container.bottom {
        flex-direction: column;
        padding: 0.3rem 0.4rem 0.4rem 0.2rem;
    }

    .menu-quickaccess {
        margin: 0.2rem 0 0.2rem -0.25rem;
    }

    .static-container {
        z-index: 2;
    }

    .preview-window {
        display: flex;
        position: relative;
        flex-direction: column;
        height: 10rem;
        flex-shrink: 0;
        flex-grow: 0;

        background-color: #1f1c26;
        border-radius: 0.2rem;
        margin-bottom: 0.4rem;
        padding: 0.5rem 0.6rem 0rem 0.6rem;
    }

    .preview-node {
        display: flex;
        justify-content: center;
        align-items: center;
    }

    .preview-node,
    .preview-node .node-vector {
        height: 75%;
        transform: scale(1.1);
    }

    .preview-title-wrapper {
        position: relative;
        left: 0;
        right: 0;
        height: 1.3rem;
        flex-shrink: 0;
        flex-grow: 0;
        margin: 0 auto;
        width: 70%;
    }

    .preview-bottom-wrapper {
        display: flex;
        position: relative;
        justify-content: center;
        align-items: center;
        flex-grow: 0;
        left: 0;
        right: 0;
        flex-shrink: 0;
        margin: 0 auto;
        width: 80%;
    }

    .selection-idx {
        height: 0.25rem;
        width: 0.25rem;
        margin: 0.3rem 3.5%;
        background-color: rgba(255, 255, 255, 0.116);
        border-radius: 0.3rem;
    }

    .selection-idx.active {
        transform: scale(1.15);
        background-color: rgba(255, 255, 255, 0.493);
    }

    .navigation-group {
        display: flex;
        justify-content: space-between;
        position: absolute;
        width: 94%;
        height: 2rem;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        margin: auto;
    }

    .invisible {
        opacity: 0;
        pointer-events: none;
    }
</style>
